import { Popover } from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { Controller, useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { useParams } from "react-router-dom";

import NoData from "~/components/containers/NoData.tsx";
import ButtonWithSpinner from "~/components/ui/button/Button.tsx";
import { messageFromError } from "~/helpers/messageFromError.ts";
import { getBrandName } from "~/utils/white-label";

import {
  CertificateOrder,
  Certificate as CertificateType,
  Domain,
  DomainCertificateAttributes,
} from "../../../api/Api";
import { apiClient } from "../../../api/ApiClient";
import { Icon } from "../../../assets/icons/Icon";
import { useWizard } from "../../../contexts/WizardContext";
import Button from "../../mantine/button/Button";
import Spinner from "../../ui/spinner/Spinner";
import styles from "./Wizzard.module.scss";

export type UICertificate = CertificateType & {
  current?: boolean;
};

type CertsParams = NonNullable<
  Parameters<typeof apiClient.domains.certificatesDetail>[1]
>;
type CertsQ = NonNullable<CertsParams["q"]>;
type ExtendedCertsQuery = CertsQ & {
  not_before_lteq?: string;
  not_after_gteq?: string;
};

const fileTypes = ["CERT"];
const keyFileTypes = ["KEY"];

type CertificateProps = {
  isWizzard?: boolean;
};

type CertificateProvider = "letsencrypt" | "custom" | "custom-upload";

interface CertificateFormValues {
  certificateProvider: CertificateProvider;
  certificateId: number | null;
  certificateFile: File | null;
  certificateKey: File | null;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);

  return date.toISOString().split("T")[0];
};

const IN_PROGRESS_STATUSES = [
  "pending",
  "ordered",
  "verified",
  "obtained",
  "suspended",
];
const FAILED_STATUS = "failed";

const fileCertMessage =
  "Drag & drop your certificate file here or click to browse";
const fileKeyMessage = "Drag & drop your key file here or click to browse";
const COLUMNS_COUNT = 4;
const Certificate = ({ isWizzard = false }: CertificateProps) => {
  const [isFileCertError, setIsFileCertError] = useState("");
  const [isFileKeyError, setIsFileKeyError] = useState("");
  const [openedOrderId, setOpenedOrderId] = useState<number | null>(null);
  const { domainId } = useParams<{ domainId: string }>();
  const { navigateToPrevStep, updateDomain, domain } = useWizard();
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
  const isCertReady = (d?: Domain) => Boolean(d?.status?.certificate);

  const fetchDomain = async (): Promise<Domain | undefined> => {
    const res = await apiClient.domains.statusDetail(Number(domainId));

    return res?.data?.domain;
  };

  /**
   * Infinite polling with interval between requests.
   * Keeps checking the domain until predicate(domain) returns true.
   */
  const pollDomainUntil = async (
    predicate: (d?: Domain) => boolean,
    { intervalMs = 4000 } = {},
  ): Promise<Domain | undefined> => {
    let last: Domain | undefined;

    while (true) {
      try {
        last = await fetchDomain();
        if (predicate(last)) return last;
      } catch (error) {
        console.error("Polling error:", error);
      }

      await sleep(intervalMs);
    }
  };

  const {
    data: certificates,
    isLoading: isCertificatesLoading,
    isFetching,
    refetch: refetchCertificates,
  } = useQuery<UICertificate[]>({
    queryKey: ["certificates", "assigned", "index", domainId],
    queryFn: async () => {
      const now = new Date().toISOString();

      const [currentCertificatesResult, customCertificatesResult] =
        await Promise.all([
          apiClient.domains.certificatesAssignedDetail(Number(domainId)),
          apiClient.domains.certificatesDetail(Number(domainId), {
            q: {
              issuer_eq: "custom",
              not_before_lteq: now,
              not_after_gteq: now,
            } as ExtendedCertsQuery,
          }),
        ]);

      const current = (currentCertificatesResult?.data?.certificates ?? []).map(
        (c) => ({
          ...c,
          current: true, // add 'current' flag
        }),
      );
      const custom = customCertificatesResult?.data?.certificates ?? [];

      // merge and uniq
      return Array.from(
        // Note: order matters! Put custom first, so current (with current:true) overwrites duplicates
        new Map([...custom, ...current].map((c) => [c.id, c])).values(),
      );
    },
    select: (certificates) =>
      [...certificates].sort((a, b) => {
        // 1 not_after DESC
        const aAfter = new Date(a.not_after).getTime() || 0;
        const bAfter = new Date(b.not_after).getTime() || 0;
        if (bAfter !== aAfter) return bAfter - aAfter;

        // 2 site_id ASC (nulls first)
        if (a.site_id === null && b.site_id !== null) return -1;
        if (a.site_id !== null && b.site_id === null) return 1;
        if (a.site_id !== b.site_id) return (a.site_id ?? 0) - (b.site_id ?? 0);

        // 3 tenant_hostname_id ASC (nulls first)
        if (a.tenant_hostname_id === null && b.tenant_hostname_id !== null)
          return -1;
        if (a.tenant_hostname_id !== null && b.tenant_hostname_id === null)
          return 1;
        if (a.tenant_hostname_id !== b.tenant_hostname_id)
          return (a.tenant_hostname_id ?? 0) - (b.tenant_hostname_id ?? 0);

        return 0;
      }),
  });

  const { data: certificateOrders, refetch: refetchCertificateOrders } =
    useQuery({
      queryKey: ["certificateOrders", domainId],
      refetchOnWindowFocus: false,
      queryFn: async () => {
        const response = await apiClient.domains.certificateOrdersDetail(
          Number(domainId),
          {
            s: "created_at DESC",
            q: {
              // @ts-expect-error BE types - missing input field
              status_in: [...IN_PROGRESS_STATUSES, FAILED_STATUS],
            },
          },
        );

        return response?.data?.certificate_orders || [];
      },
      refetchInterval: (data) => {
        const hasInProgressOrders = data?.state?.data?.some(
          (order: CertificateOrder) =>
            IN_PROGRESS_STATUSES.includes(order?.status),
        );

        return hasInProgressOrders ? 5000 : false;
      },
      select: (orders) => {
        if (!Array.isArray(orders)) return [];

        // IN_PROGRESS — keep all
        const inProgress = orders.filter((o) =>
          IN_PROGRESS_STATUSES.includes(o.status),
        );

        // FAILED — take last 3 by created_at
        const failedLatest = orders
          .filter((o) => o.status === FAILED_STATUS)
          .sort((a, b) => {
            const aTime = new Date(a.created_at).getTime() || 0;
            const bTime = new Date(b.created_at).getTime() || 0;

            return bTime - aTime;
          })
          .slice(0, 3);

        // Combine and sort by created_at DESC
        return [...inProgress, ...failedLatest].sort((a, b) => {
          const aTime = new Date(a.created_at).getTime() || 0;
          const bTime = new Date(b.created_at).getTime() || 0;

          return bTime - aTime;
        });
      },
    });

  const { mutate: createCertificate, isPending } = useMutation({
    mutationFn: async (payload: DomainCertificateAttributes) => {
      return await apiClient.domains.certificateCreate(
        Number(domainId),
        payload,
      );
    },
    onError: (error) => {
      const message = messageFromError(error) ?? "";
      enqueueSnackbar(`Failed to set up certificate: ${message}`, {
        variant: "error",
        persist: true,
      });
      setValue("certificateFile", null, { shouldDirty: true });
      setValue("certificateKey", null, { shouldDirty: true });
    },
    onSuccess: async () => {
      try {
        const finalDomain = await pollDomainUntil(isCertReady, {
          intervalMs: 4000,
        });

        if (finalDomain && isCertReady(finalDomain)) {
          updateDomain(finalDomain, {
            navigateToNextStep: isWizzard,
          });

          enqueueSnackbar("Certificate settings saved!", {
            variant: "success",
          });
        } else {
          enqueueSnackbar("Certificate settings are changing…", {
            variant: "info",
          });
        }
      } catch (error) {
        console.error("Error retrying domain status:", error);
        setValue("certificateFile", null, { shouldDirty: true });
        setValue("certificateKey", null, { shouldDirty: true });
      }
    },
    onSettled: async () => {
      if (!isWizzard) {
        await Promise.all([refetchCertificates(), refetchCertificateOrders()]);
      }
    },
  });

  const { register, handleSubmit, watch, setValue, control, getValues } =
    useForm<CertificateFormValues>({
      defaultValues: {
        certificateProvider:
          domain?.certificate_provider === "custom" ? "custom" : "letsencrypt",
        certificateId: null,
        certificateFile: null,
        certificateKey: null,
      },
    });

  const selectedProvider = watch("certificateProvider");
  // used only for custom
  const selectedCertificateId = watch("certificateId");
  const certificateFile = watch("certificateFile");
  const certificateKey = watch("certificateKey");

  const isCustomExisting = selectedProvider === "custom";
  const isCustomUpload = selectedProvider === "custom-upload";
  const requiresExistingId = isCustomExisting && !selectedCertificateId;
  const requiresFiles = isCustomUpload && !(certificateFile && certificateKey);
  const isFileUploadersDisabled = !isCustomUpload;

  const applyCustomCert = (certificate: UICertificate) => {
    setValue("certificateProvider", "custom", { shouldDirty: true });
    setValue("certificateId", certificate.id, { shouldDirty: true });
    setValue("certificateFile", null, { shouldDirty: true });
    setValue("certificateKey", null, { shouldDirty: true });
  };

  const onSubmit = (data: CertificateFormValues) => {
    const providerForApi: "letsencrypt" | "custom" =
      data.certificateProvider === "letsencrypt" ? "letsencrypt" : "custom";

    const payload: DomainCertificateAttributes = {
      certificate_provider: providerForApi,
    };

    if (data.certificateProvider === "custom") {
      if (!data.certificateId) {
        enqueueSnackbar("Select an existing custom certificate", {
          variant: "error",
          persist: true,
        });

        return;
      }

      payload.certificate_id = data.certificateId;
    }

    if (data.certificateProvider === "custom-upload") {
      if (!(data.certificateFile && data.certificateKey)) {
        enqueueSnackbar("Upload both .cert and .key files", {
          variant: "error",
          persist: true,
        });

        return;
      }

      payload.certificate_file = data.certificateFile;
      payload.certificate_key = data.certificateKey;
    }

    createCertificate(payload);
  };

  const handleBack = () => {
    navigateToPrevStep();
  };

  useEffect(() => {
    if (!certificates?.length || !domain) return;

    const domainProvider = domain.certificate_provider;
    if (!domainProvider) return; // cannot continue if it's dropped for some reason

    const provider = getValues("certificateProvider");

    // sync form cert_provider with domain new updated value
    if (domainProvider !== provider) {
      setValue("certificateProvider", domainProvider, { shouldDirty: true });
    }

    // reset certificateId
    if (domainProvider === "letsencrypt") {
      setValue("certificateId", null, { shouldDirty: true });
    } else if (domainProvider === "custom") {
      // set certificateId for custom
      const matched = certificates.find(
        (c) => c.current && c.issuer === "custom",
      );
      if (matched) {
        setValue("certificateId", matched.id, { shouldDirty: true });
      }
    }

    setValue("certificateFile", null, { shouldDirty: true });
    setValue("certificateKey", null, { shouldDirty: true });
  }, [domain?.certificate_provider, certificates]);

  const selectedCustomCert = useMemo(
    () => certificates?.find((c) => c.id === selectedCertificateId) ?? null,
    [certificates, selectedCertificateId],
  );

  if (isCertificatesLoading) {
    return <Spinner />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div>
        <div className={styles.stepContentListText}>
          {getBrandName()} prioritizes your website's safety, ensuring it's
          trustworthy for your customers. Rest assured, obtaining a certificate
          is effortless with {getBrandName()} as it's automatically issued for
          you from Let's Encrypt. These certificates are globally recognized and
          trusted worldwide.
        </div>

        <div className={styles.stepContentWrapper}>
          <div>
            <div className={styles.stepContentTitle}>Current Certificates</div>
            <table className={styles.certificatesTable}>
              <thead>
                <tr>
                  <th>Issuer</th>
                  <th>Names</th>
                  <th>Validity</th>
                  <th>Current</th>
                </tr>
              </thead>
              <tbody>
                {isCertificatesLoading ? (
                  <>
                    <tr>
                      <td className={styles.skeletonTd} colSpan={COLUMNS_COUNT}>
                        <Skeleton className={styles.skeletonCell} />
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.skeletonTd} colSpan={COLUMNS_COUNT}>
                        <Skeleton className={styles.skeletonCell} />
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.skeletonTd} colSpan={COLUMNS_COUNT}>
                        <Skeleton className={styles.skeletonCell} />
                      </td>
                    </tr>
                  </>
                ) : certificates && certificates.length === 0 && !isFetching ? (
                  <tr>
                    <td colSpan={COLUMNS_COUNT}>
                      <NoData title="No certificates added yet" />
                    </td>
                  </tr>
                ) : (
                  <>
                    {certificates?.map((certificate, idx) => {
                      const alt_names = Array.isArray(certificate.alt_names)
                        ? certificate.alt_names
                        : [];

                      const names = Array.from(
                        new Set([certificate.subject, ...alt_names]),
                      );
                      const isApplyBtnEnabled =
                        certificate.issuer === "custom" &&
                        (!selectedCertificateId ||
                          selectedCertificateId !== certificate.id);

                      return (
                        <tr key={`${certificate.id}-${idx}`}>
                          <td>{certificate.issuer_name}</td>
                          <td>
                            <div className={styles.certificateField}>
                              {names.map((name: string, index: number) => (
                                <div key={index}>{name}</div>
                              ))}
                            </div>
                          </td>
                          <td>
                            {formatDate(certificate.not_before)} -{" "}
                            {formatDate(certificate.not_after)}
                          </td>
                          <td>
                            <Button
                              variant="outlined"
                              disabled={!isApplyBtnEnabled}
                              className={clsx(styles.link, {
                                [styles.linkDisabled]: !isApplyBtnEnabled,
                              })}
                              onClick={() => applyCustomCert(certificate)}
                            >
                              Apply
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>

            {certificateOrders && certificateOrders.length > 0 && (
              <div className={styles.ordersWrapper}>
                <div className={styles.stepContentTitle}>Last Orders</div>

                <table className={styles.certificatesTable}>
                  <thead>
                    <tr>
                      <th>Issuer</th>
                      <th>Names</th>
                      <th>Expires</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificateOrders?.map((order, idx) => {
                      return (
                        <tr key={`${order.id}-${idx}`}>
                          <td>{order.issuer}</td>
                          <td>
                            <div className={styles.certificateField}>
                              {order.identifiers.map(
                                (name: string, index: number) => (
                                  <div key={index}>{name}</div>
                                ),
                              )}
                            </div>
                          </td>
                          <td>{formatDate(order.expires_at)}</td>
                          <td>
                            <div className={styles.orderStatusCell}>
                              <span>{order.status}</span>

                              {order.error && (
                                <Popover
                                  offset={1}
                                  position="top-start"
                                  withinPortal={false}
                                  opened={openedOrderId === order.id}
                                  onChange={(opened) =>
                                    setOpenedOrderId(opened ? order.id : null)
                                  }
                                  zIndex={1000}
                                  styles={{
                                    dropdown: {
                                      minWidth: "220px",
                                      maxWidth: "320px",
                                    },
                                  }}
                                >
                                  <Popover.Target>
                                    <button
                                      type="button"
                                      className={styles.statusHelpButton}
                                      onClick={() =>
                                        setOpenedOrderId(
                                          openedOrderId === order.id
                                            ? null
                                            : order.id,
                                        )
                                      }
                                    >
                                      <Icon
                                        iconName="QuestionMarkInCircleIcon"
                                        width="1rem"
                                        height="1rem"
                                      />
                                    </button>
                                  </Popover.Target>

                                  <Popover.Dropdown
                                    className={styles.statusPopover}
                                  >
                                    <p>{order.error}</p>
                                  </Popover.Dropdown>
                                </Popover>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className={styles.stepContentRadioGroup}>
            <div className={styles.radioOption}>
              <input
                type="radio"
                id="letsencrypt-ssl"
                value="letsencrypt"
                {...register("certificateProvider")}
                // @ts-expect-error TS2339 non-existing className
                className={styles.radioInput}
              />
              <label
                htmlFor="letsencrypt-ssl"
                // @ts-expect-error TS2339 non-existing className
                className={styles.radioLabel}
              >
                Automatically issue and renew with Let’s Encrypt
              </label>
            </div>

            {selectedCustomCert && (
              <div className={styles.radioOption}>
                <input
                  type="radio"
                  id="custom-certificate"
                  value="custom"
                  {...register("certificateProvider")}
                />
                <label
                  htmlFor="custom-certificate"
                  // @ts-expect-error TS2339 non-existing className
                  className={styles.radioLabel}
                >
                  {`Keep existing from ${selectedCustomCert.issuer_name ?? "Custom"}`}
                </label>
              </div>
            )}

            <div className={styles.radioOption}>
              <input
                type="radio"
                id="custom-upload"
                value="custom-upload"
                {...register("certificateProvider")}
              />
              <label htmlFor="custom-upload">Upload my own certificate</label>
            </div>

            <div className={styles.stepContentFileUploads}>
              <label
                htmlFor="certificateFile"
                className={clsx({
                  [styles.withOpacity]: isFileUploadersDisabled,
                })}
              >
                .cert file
              </label>
              <div className={styles.fileUploader}>
                <Controller
                  name="certificateFile"
                  control={control}
                  render={() => (
                    <FileUploader
                      handleChange={(file: File) =>
                        setValue("certificateFile", file)
                      }
                      name="certificateFile"
                      types={fileTypes}
                      classes={styles.fileUploader}
                      disabled={isFileUploadersDisabled}
                      onTypeError={() =>
                        setIsFileCertError(
                          "Please upload a valid certificate file (.CERT format)",
                        )
                      }
                    >
                      {/*
                     // @ts-expect-error TS2551 non-existing className */}
                      <div className={styles.fileUploaderText}>
                        {certificateFile?.name
                          ? certificateFile.name
                          : isFileCertError
                            ? ""
                            : fileCertMessage}
                        {isFileCertError && !certificateFile?.name && (
                          <div className={styles.fileUploaderTextError}>
                            {isFileCertError}
                          </div>
                        )}
                      </div>
                    </FileUploader>
                  )}
                />
              </div>

              <label
                htmlFor="certificateKey"
                className={clsx({
                  [styles.withOpacity]: isFileUploadersDisabled,
                })}
              >
                .key file
              </label>
              <div className={styles.fileUploader}>
                <Controller
                  name="certificateKey"
                  control={control}
                  render={() => (
                    <FileUploader
                      handleChange={(file: File) =>
                        setValue("certificateKey", file)
                      }
                      name="keyFile"
                      types={keyFileTypes}
                      classes={styles.fileUploader}
                      disabled={isFileUploadersDisabled}
                      onTypeError={() =>
                        setIsFileKeyError(
                          "Please upload a valid key file (.KEY format)",
                        )
                      }
                    >
                      {/*
                     // @ts-expect-error TS2551 non-existing className */}
                      <div className={styles.fileUploaderText}>
                        {certificateKey?.name
                          ? certificateKey?.name
                          : isFileKeyError
                            ? ""
                            : fileKeyMessage}
                        {isFileKeyError && !certificateFile?.name && (
                          <div className={styles.fileUploaderTextError}>
                            {isFileKeyError}
                          </div>
                        )}
                      </div>
                    </FileUploader>
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.stepContentFooter}>
          {isWizzard && (
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={isPending}
            >
              Back
            </Button>
          )}
          <ButtonWithSpinner
            type="submit"
            withSpinner={isPending}
            disabled={isPending || requiresExistingId || requiresFiles}
          >
            {isWizzard ? "Continue" : "Save"}
          </ButtonWithSpinner>
        </div>
      </div>
    </form>
  );
};

export default Certificate;
